import { canPass, legalBids, legalPlays } from "@meksiko/game-engine";
import {
  cardId,
  REACTIONS,
  type Card,
  type DealView,
  type LeaderboardEntryView,
  type MatchView,
  type PlayedCard,
  type SeatIndex,
  type SeatInfo,
  type Suit,
} from "@meksiko/shared-types";
import type { WaitingRoomInfo } from "../net/connection.js";
import { useAppStore } from "../store/appStore.js";
import { CARD_BACK_URL, cardImageUrl, SUIT_COLOR, SUIT_UI_COLOR } from "../cards/assets.js";
import { LANGUAGES, t } from "../i18n/index.js";
import { actingSeat } from "../matchViewHelpers.js";

type AppState = ReturnType<typeof useAppStore.getState>;

let selectedDiscard: string[] = [];
let selectedDiscardDealNumber: number | null = null;

// Brief pause after a deal ends, before the next deal's bidding UI becomes
// interactive — no popup to dismiss, just long enough to notice the score
// change. The server has already moved on to the next deal by the time we
// see this (game-engine's match.ts advances straight through), so this is
// purely a display-side hold; the same setTimeout-triggers-a-re-render
// pattern as the trick-hold animation below.
const DEAL_END_PAUSE_MS = 4000;
let dealPauseUntil = 0;
let dealPausedForDealNumber = -1;

// Trick-area animation bookkeeping (purely cosmetic — see renderTrickArea).
//
// Every render() call rebuilds the whole screen's innerHTML from scratch
// (see main.ts), including a 250ms tick that only exists to keep the turn
// countdown numbers moving. That tick has nothing to do with trick cards,
// but because it recreates the DOM nodes, any CSS keyframe animation class
// still present on a card would restart from frame 0 on every single one of
// those ticks — a card mid-flight would visibly jump back and replay several
// times a second instead of playing once smoothly. To avoid that, every
// animation trigger here is "consumed" (cleared from module state) the
// instant it's rendered once, so a later incidental re-render always finds
// the card already in its resting/final state instead of re-triggering it.
const TRICK_HOLD_MS = 700; // how long a just-finished trick stays fully visible before sweeping away
const TRICK_SWEEP_MS = 320; // must match the cardSweepTo* keyframe duration in style.css

interface HeldTrick {
  cards: PlayedCard[];
  winner: SeatIndex;
  // "hold": frozen, fully visible. "sweep-start": play the fly-away
  // animation exactly once. "sweep-done": animation already played — render
  // the cards in their final (invisible) state so a stray re-render can't
  // replay it.
  phase: "hold" | "sweep-start" | "sweep-done";
}

let heldTrick: HeldTrick | null = null;
let heldTrickTimer: ReturnType<typeof setTimeout> | null = null;
let lastSeenDealNumber = -1;
let lastSeenCompletedCount = -1;
let lastCurrentTrickRef: DealView["currentTrick"] | null = null;
let enteringCardKey: string | null = null;

const SUITS: Suit[] = ["diamonds", "hearts", "clubs", "leaves"];

// Display order for the player's own hand: karo, tref, srce, list (diamonds,
// clubs, hearts, leaves) left to right, strongest card first within each
// suit — purely a client-side presentation choice, has no bearing on the
// server-authoritative hand order or on legality checks.
const HAND_SUIT_ORDER: Record<Suit, number> = { diamonds: 0, clubs: 1, hearts: 2, leaves: 3 };

function sortHandForDisplay(hand: Card[]): Card[] {
  return [...hand].sort((a, b) => HAND_SUIT_ORDER[a.suit] - HAND_SUIT_ORDER[b.suit] || b.rank - a.rank);
}

// Every render() call rebuilds this whole subtree from scratch (see main.ts),
// including on every other player's turn/action — not just the viewer's own.
// Without the suppress-transitions guard, a card the mouse happens to be
// resting over gets destroyed and replaced by a structurally identical new
// <img>; since that fresh element already matches :hover at the moment it's
// inserted, browsers still play its `transition: transform` from the resting
// to the hovered state, visibly "jumping" the lift animation on every
// incidental re-render instead of just once on a genuine mouse-enter. Forcing
// a reflow while transitions are off applies that to the new nodes' very
// first style resolution, then re-enabling on the next frame leaves real
// future hover/focus transitions working normally.
function el(html: string): void {
  const app = document.getElementById("app")!;
  app.classList.add("suppress-transitions");
  app.innerHTML = html;
  void app.offsetHeight; // force a synchronous reflow before transitions come back
  requestAnimationFrame(() => app.classList.remove("suppress-transitions"));
}

function cardImg(card: Card, opts: { classes?: string[]; dataCard?: string } = {}): string {
  const classes = (opts.classes ?? []).join(" ");
  const dataAttr = opts.dataCard ? `data-action="play-card" data-card="${opts.dataCard}"` : "";
  return `<img src="${cardImageUrl(card)}" alt="${t("game.cardAlt")}" class="${classes}" ${dataAttr} />`;
}

export function renderApp(state: AppState): void {
  switch (state.screen) {
    case "loading":
      renderLoading();
      break;
    case "login":
      renderLoginScreen(state);
      break;
    case "lobby":
      renderLobby(state);
      break;
    case "waiting":
      renderWaiting(state);
      break;
    case "game":
      if (state.matchView) renderGame(state, state.matchView);
      else renderLoading();
      break;
    case "leaderboard":
      renderLeaderboardScreen(state);
      break;
    case "profile":
      renderProfileScreen(state);
      break;
    case "error":
      renderErrorScreen(state);
      break;
  }
}

function renderLoading(): void {
  el(`<div class="centered-screen"><p>${t("common.loading")}</p></div>`);
}

// Language dropdown trigger + menu, shared by the login screen (its own
// absolutely-positioned corner wrapper — see .login-corner in style.css,
// since that screen has no topbar at all) and the lobby (a plain flex
// sibling before .profile-chip in .lobby-topbar). Modeled directly on the
// existing reactionMenuOpen/renderReactionDialog open-close pattern.
function renderLanguageSwitcher(state: AppState): string {
  const current = LANGUAGES.find((l) => l.code === state.language) ?? LANGUAGES[0]!;
  // .language-menu-overlay and .language-menu are SIBLINGS, not
  // parent/child: the overlay is just a full-viewport transparent
  // click-catcher (closes the menu on outside click, same idea as
  // .reaction-overlay), while the menu itself is positioned absolutely
  // relative to .language-switcher (position:relative) so it anchors right
  // under the trigger regardless of where the switcher sits on screen
  // (lobby topbar row vs. the login screen's corner wrapper). Nesting the
  // menu inside the fixed/full-viewport overlay would instead position it
  // relative to the *viewport*, not the trigger.
  const menuHtml = state.languageMenuOpen
    ? `
      <div class="language-menu-overlay" data-action="close-language-menu"></div>
      <div class="language-menu">
        ${LANGUAGES.map(
          (l) => `
          <button class="language-option ${l.code === state.language ? "active" : ""}" data-action="select-language" data-lang="${l.code}">
            <span class="language-option-flag">${l.flag}</span>
            <span class="language-option-name">${escapeHtml(l.nativeName)}</span>
            <span class="language-option-abbrev">${l.abbrev}</span>
          </button>
        `,
        ).join("")}
      </div>
    `
    : "";
  return `
    <div class="language-switcher">
      <button class="language-trigger" data-action="toggle-language-menu" aria-label="${t("languageSwitcher.ariaLabel")}">
        <span class="language-trigger-flag">${current.flag}</span>
        <span class="language-trigger-abbrev">${current.abbrev}</span>
      </button>
      ${menuHtml}
    </div>
  `;
}

function renderLoginScreen(state: AppState): void {
  el(`
    <div class="login-corner">${renderLanguageSwitcher(state)}</div>
    <div class="centered-screen login-screen">
      <h1>Meksiko</h1>
      <p class="login-tagline">${t("login.tagline")}</p>
      <p>${t("login.subtitle")}</p>
      <button data-action="login-facebook">${t("login.continueFacebook")}</button>
      <button data-action="login-google">${t("login.continueGoogle")}</button>
      <button data-action="login-email">${t("login.continueEmail")}</button>
      <nav class="login-links">
        <a href="/pravila/">${t("login.linkRules")}</a>
        <a href="/strategija/">${t("login.linkStrategy")}</a>
        <a href="/o-igri/">${t("login.linkAbout")}</a>
        <a href="/privacy/">${t("login.linkPrivacy")}</a>
      </nav>
    </div>
  `);
}

function renderErrorScreen(state: AppState): void {
  el(`
    <div class="centered-screen">
      <p>${t("common.errorPrefix", { message: escapeHtml(state.lastError ?? t("common.unknownError")) })}</p>
      <button data-action="reload">${t("common.retry")}</button>
    </div>
  `);
}

function renderLobby(state: AppState): void {
  el(`
    <div class="lobby-topbar">
      ${renderLanguageSwitcher(state)}
      <div class="profile-chip">
        ${renderSelfAvatar(state, "avatar-xl")}
        <span class="profile-chip-name">${escapeHtml(state.displayName)}</span>
        <span class="coin-balance">🪙 ${state.coinBalance}</span>
      </div>
    </div>
    <div class="scroll-screen">
      <div class="lobby-leaderboard-panel">
        <h2>${t("leaderboard.title")}${seasonLabel(state)}</h2>
        <div class="lobby-leaderboard-scroll">
          ${renderLeaderboardTable(state.leaderboard, state.userId, state.photoUrl, 10, t("leaderboard.emptySeason"))}
        </div>
        <button class="secondary" data-action="open-leaderboard">${t("common.details")}</button>
      </div>

      <div class="lobby-main">
        <h1>Meksiko</h1>
        <p>${t("lobby.entryFee", { fee: state.matchEntryFee })}</p>
        <button data-action="join-queue">${state.activeMatchId ? t("lobby.resumeMatch") : t("lobby.playButton")}</button>
        <button class="secondary" data-action="open-settings">${t("common.settingsTitle")}</button>
        <button class="secondary" data-action="open-profile">${t("common.profile")}</button>
        <button class="secondary" data-action="open-rules">${t("common.rules")}</button>
        <button class="secondary" data-action="share-invite">${t("lobby.inviteFriend")}</button>
        ${state.lastError ? `<p class="error-banner" style="position:static">${escapeHtml(state.lastError)}</p>` : ""}
      </div>
    </div>
    ${state.showInsufficientFundsModal ? renderInsufficientFundsModal(state) : ""}
    ${state.rulesOpen ? renderRulesModal() : ""}
    ${state.settingsOpen ? renderSettingsModal(state) : ""}
  `);
}

function renderRulesModal(): string {
  return `
    <div class="modal-overlay" data-action="close-rules">
      <div class="modal-panel" data-action="noop">
        <h2>${t("rules.title")}</h2>

        <h3>${t("rules.cardsHeading")}</h3>
        <p>${t("rules.cardsBody")}</p>

        <h3>${t("rules.dealingHeading")}</h3>
        <p>${t("rules.dealingBody")}</p>

        <h3>${t("rules.biddingHeading")}</h3>
        <p>${t("rules.biddingBody")}</p>

        <h3>${t("rules.talonHeading")}</h3>
        <p>${t("rules.talonBody")}</p>

        <h3>${t("rules.mexicoHeading")}</h3>
        <p>${t("rules.mexicoBody")}</p>

        <h3>${t("rules.tricksHeading")}</h3>
        <p>${t("rules.tricksBody")}</p>

        <h3>${t("rules.scoringHeading")}</h3>
        <p>${t("rules.scoringBody")}</p>

        <h3>${t("rules.matchEndHeading")}</h3>
        <p>${t("rules.matchEndBody")}</p>

        <div class="close-row"><button class="secondary" data-action="close-rules">${t("common.close")}</button></div>
      </div>
    </div>
  `;
}

function renderSettingsModal(state: AppState): string {
  return `
    <div class="modal-overlay" data-action="close-settings">
      <div class="modal-panel" data-action="noop">
        <h2>${t("common.settingsTitle")}</h2>
        <button class="secondary" data-action="toggle-mute">${state.muted ? t("settings.soundOff") : t("settings.soundOn")}</button>
        <div class="display-name-editor">
          <label for="display-name-input">${t("settings.displayNameLabel")}</label>
          <input id="display-name-input" type="text" maxlength="20" value="${escapeHtml(state.displayName)}" />
          <button class="secondary" data-action="save-display-name">${t("settings.saveDisplayName")}</button>
          ${state.displayNameError ? `<p class="error-text">${escapeHtml(state.displayNameError)}</p>` : ""}
        </div>
        ${state.screen !== "game" ? `<button class="secondary" data-action="logout">${t("settings.logout")}</button>` : ""}
        <div class="close-row"><button class="secondary" data-action="close-settings">${t("common.close")}</button></div>
      </div>
    </div>
  `;
}

function renderInsufficientFundsModal(state: AppState): string {
  const missing = state.matchEntryFee - state.coinBalance;
  return `
    <div class="ad-overlay">
      <div class="ad-modal">
        <p>${t("lobby.insufficientFunds", { missing })}</p>
        <button data-action="watch-ad">${t("lobby.watchAd")}</button>
        <button class="secondary" data-action="close-insufficient-funds">${t("common.close")}</button>
        ${state.lastError ? `<p class="error-banner" style="position:static">${escapeHtml(state.lastError)}</p>` : ""}
      </div>
    </div>
  `;
}

function seasonLabel(state: AppState): string {
  const [year, month] = (state.leaderboard?.season ?? "").split("-");
  return year && month ? ` — ${month}/${year}` : "";
}

type RankedData = { entries: LeaderboardEntryView[]; me: { points: number; matchesPlayed: number; rank: number | null } | null };

// Shared by the lobby's mini preview, and both tabs (season/global) of the
// full leaderboard screen — GAME_SPEC.md §14. Always surfaces the viewer's
// own standing even when it falls outside the shown rows, per `data.me`.
function renderLeaderboardTable(
  data: RankedData | null,
  userId: string | null,
  myPhotoUrl: string | null,
  maxRows: number,
  emptyMessage: string,
): string {
  if (!data) return `<p>${t("common.loadingEllipsis")}</p>`;
  if (data.entries.length === 0) {
    return `<p>${emptyMessage}</p>`;
  }

  const shown = data.entries.slice(0, maxRows);
  const meInShown = shown.some((e) => e.userId === userId);

  const row = (e: { rank: number; userId: string; displayName: string; photoUrl: string | null; points: number; matchesPlayed: number }) => `
    <tr class="${e.userId === userId ? "me" : ""}">
      <td>${e.rank}</td>
      <td class="player-cell">${renderAvatar(e.photoUrl, e.displayName, "avatar-sm")}${escapeHtml(e.displayName)}</td>
      <td>${e.points}</td>
      <td>${e.matchesPlayed}</td>
    </tr>
  `;

  let extraRow = "";
  if (!meInShown && data.me) {
    if (data.me.rank !== null) {
      extraRow =
        `<tr class="gap-row"><td colspan="4">⋯</td></tr>` +
        row({
          rank: data.me.rank,
          userId: userId ?? "",
          displayName: t("leaderboard.you"),
          photoUrl: myPhotoUrl,
          points: data.me.points,
          matchesPlayed: data.me.matchesPlayed,
        });
    } else {
      extraRow = `<tr><td colspan="4">${t("leaderboard.yourScoreUnranked", { points: data.me.points, matchesPlayed: data.me.matchesPlayed })}</td></tr>`;
    }
  }

  return `
    <table class="leaderboard-table">
      <thead><tr><th>#</th><th>${t("leaderboard.colPlayer")}</th><th>${t("leaderboard.colPoints")}</th><th>${t("leaderboard.colMatches")}</th></tr></thead>
      <tbody>${shown.map(row).join("")}${extraRow}</tbody>
    </table>
  `;
}

function renderLeaderboardScreen(state: AppState): void {
  const tab = state.leaderboardTab;
  const [year, month] = (state.leaderboard?.season ?? "").split("-");
  const seasonTabLabel = year && month ? t("leaderboard.seasonTabWithDate", { month, year }) : t("leaderboard.seasonTabDefault");

  const tableHtml =
    tab === "season"
      ? renderLeaderboardTable(
          state.leaderboard,
          state.userId,
          state.photoUrl,
          state.leaderboard?.entries.length ?? 100,
          t("leaderboard.emptySeason"),
        )
      : renderLeaderboardTable(
          state.globalLeaderboard,
          state.userId,
          state.photoUrl,
          state.globalLeaderboard?.entries.length ?? 100,
          t("leaderboard.emptyGlobal"),
        );

  el(`
    <div class="topbar">
      <button class="secondary" data-action="nav-lobby">${t("common.back")}</button>
      <span>${t("leaderboard.title")}</span>
      <button class="secondary" data-action="open-profile">${t("common.profile")}</button>
    </div>
    <div class="scroll-screen">
      <div class="leaderboard-tabs">
        <button class="tab-button ${tab === "season" ? "active" : ""}" data-action="leaderboard-tab-season">${seasonTabLabel}</button>
        <button class="tab-button ${tab === "global" ? "active" : ""}" data-action="leaderboard-tab-global">${t("leaderboard.globalTab")}</button>
      </div>
      ${tableHtml}
    </div>
  `);
}

function renderProfileScreen(state: AppState): void {
  const stats = state.profileStats;
  const medals = state.profileMedals;

  const statsHtml = stats
    ? `
      <div class="stats-grid">
        <div class="stat-card"><div class="stat-value">${stats.matchesPlayed}</div><div class="stat-label">${t("profile.matchesPlayed")}</div></div>
        <div class="stat-card gold"><div class="stat-value">${stats.winPct}%</div><div class="stat-label">${t("profile.firstPlace", { wins: stats.wins })}</div></div>
        <div class="stat-card silver"><div class="stat-value">${stats.secondPct}%</div><div class="stat-label">${t("profile.secondPlace", { count: stats.secondPlaces })}</div></div>
        <div class="stat-card bronze"><div class="stat-value">${stats.thirdPct}%</div><div class="stat-label">${t("profile.thirdPlace", { count: stats.thirdPlaces })}</div></div>
      </div>
      <p class="points-config-note">${t("profile.pointsConfigNote", { win: stats.pointsConfig.win, second: stats.pointsConfig.second, third: stats.pointsConfig.third })}</p>
    `
    : `<p>${t("common.loadingEllipsis")}</p>`;

  const medalsHtml =
    medals && medals.medals.length > 0
      ? `<div class="medal-row">${medals.medals
          .map((m) => `<span class="medal-badge">${t(`medal.${m.medal}`)}${m.season ? ` (${m.season})` : ""}</span>`)
          .join("")}</div>`
      : `<p>${t("profile.noMedals")}</p>`;

  const seasonHistoryHtml =
    medals && medals.seasonHistory.length > 0
      ? `
        <table class="leaderboard-table">
          <thead><tr><th>${t("profile.colSeason")}</th><th>${t("leaderboard.colPoints")}</th><th>${t("leaderboard.colMatches")}</th><th>${t("profile.colRank")}</th></tr></thead>
          <tbody>
            ${medals.seasonHistory
              .map(
                (s) => `
              <tr>
                <td>${s.season}</td>
                <td>${s.points}</td>
                <td>${s.matchesPlayed}</td>
                <td>${s.rank ?? "-"}${s.medal ? ` ${s.medal === "gold" ? "🥇" : s.medal === "silver" ? "🥈" : "🥉"}` : ""}</td>
              </tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
      `
      : "";

  el(`
    <div class="topbar">
      <button class="secondary" data-action="nav-lobby">${t("common.back")}</button>
      <span>${t("common.profile")}</span>
      <span class="coin-balance">🪙 ${state.coinBalance}</span>
    </div>
    <div class="scroll-screen">
      <div class="profile-header">
        ${renderAvatar(state.photoUrl, state.displayName, "avatar-lg")}
        <h1>${escapeHtml(state.displayName)}</h1>
      </div>
      ${statsHtml}
      <h2>${t("profile.medalsHeading")}</h2>
      ${medalsHtml}
      ${seasonHistoryHtml ? `<h2>${t("profile.seasonHistoryHeading")}</h2>${seasonHistoryHtml}` : ""}
    </div>
    ${state.deleteAccountOpen ? renderDeleteAccountModal(state) : ""}
  `);
}

function renderDeleteAccountModal(state: AppState): string {
  return `
    <div class="modal-overlay" data-action="close-delete-account">
      <div class="modal-panel" data-action="noop">
        <h2>${t("profile.deleteAccountTitle")}</h2>
        <p>${t("profile.deleteAccountWarning")}</p>
        ${state.deleteAccountError ? `<p class="error-text">${escapeHtml(state.deleteAccountError)}</p>` : ""}
        <div class="close-row">
          <button class="secondary" data-action="close-delete-account">${t("common.cancel")}</button>
          <button class="danger" data-action="confirm-delete-account" ${state.deleteAccountPending ? "disabled" : ""}>
            ${state.deleteAccountPending ? t("common.loadingEllipsis") : t("profile.deleteAccountConfirm")}
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderWaiting(state: AppState): void {
  const w = state.waiting;
  el(`
    <div class="centered-screen">
      <div class="waiting-spinner" aria-hidden="true"></div>
      <p>${w ? t("waitingRoom.waitingForPlayers", { filled: w.seatsFilled, total: w.seatsTotal }) : ""}</p>
      ${renderWaitingCountdown(w)}
      <button class="secondary" data-action="cancel-queue">${t("common.cancel")}</button>
    </div>
  `);
}

// Same idea as the in-match turn timer (GAME_SPEC.md §10): the server sends
// an absolute deadline, computed here into remaining seconds, so players see
// how long until still-empty seats get auto-filled with AI (GAME_SPEC.md §2)
// instead of just a bare spinner with no sense of when it'll resolve.
function renderWaitingCountdown(w: WaitingRoomInfo | null): string {
  if (!w || w.waitingDeadline === null) return "";
  const remainingSec = Math.max(0, Math.ceil((w.waitingDeadline - Date.now()) / 1000));
  return `<p class="waiting-countdown">${t("waitingRoom.countdown", { remainingSec })}</p>`;
}

/** Ticked every 250ms from main.ts (same pattern as tickTurnTimer) so the
 * countdown text updates without a full renderApp() in between real "waiting"
 * broadcasts. */
export function tickWaitingTimer(w: WaitingRoomInfo | null): void {
  if (!w || w.waitingDeadline === null) return;
  const label = document.querySelector<HTMLElement>(".waiting-countdown");
  if (!label) return;
  const remainingSec = Math.max(0, Math.ceil((w.waitingDeadline - Date.now()) / 1000));
  label.textContent = t("waitingRoom.countdown", { remainingSec });
}

// This seat's most recent bid this round, or null if it hasn't bid yet —
// history is a flat chronological log (packages/shared-types/src/state.ts),
// so the latest entry for a seat is found by scanning backward.
function latestBidLabel(bidding: DealView["bidding"], seat: SeatIndex): string | null {
  for (let i = bidding.history.length - 1; i >= 0; i--) {
    const entry = bidding.history[i]!;
    if (entry.seat === seat) {
      return bidValueLabel(entry.value);
    }
  }
  return null;
}

function bidValueLabel(value: number | null): string {
  return value === null ? t("game.passLabel") : value === 20 ? t("game.mexicoLabel") : String(value);
}

// Once the bidding is over, the seat that won it (and, once chosen, the
// trump they're playing with) is what everyone actually cares about — asked
// for explicitly so players can tell at a glance who's leading the deal
// without having to scroll back through the bidding history.
function isDeclarer(deal: DealView, seat: SeatIndex): boolean {
  return deal.phase !== "bidding" && deal.bidWinner === seat;
}

function declarerBadgeHtml(deal: DealView, seat: SeatIndex): string {
  return isDeclarer(deal, seat) ? `<span class="declarer-badge" title="${t("game.declarerTooltip")}">👑</span>` : "";
}

// Per-seat info shown right by a player's name — GAME_SPEC.md's "keep the
// scoreboard by the player, not in a separate bar" preference: current match
// score always, plus whichever of "latest bid" / "tricks taken this deal" is
// relevant to the deal's current phase.
function seatMetaLabel(view: MatchView, seat: SeatIndex): string {
  const { deal } = view;
  const parts = [`${view.scores[seat]}/${view.targetScore}`];
  if (deal.phase === "bidding") {
    const bid = latestBidLabel(deal.bidding, seat);
    if (bid) parts.push(t("game.bidLabel", { bid }));
  } else if (isDeclarer(deal, seat) && deal.winningBid !== null) {
    parts.push(t("game.bidLabel", { bid: bidValueLabel(deal.winningBid) }));
    if (deal.trump) {
      const suitHtml = `<span style="color:${SUIT_UI_COLOR[deal.trump]}">${t(`suit.${deal.trump}`)}</span>`;
      parts.push(t("game.trumpLabel", { suit: suitHtml }));
    }
    if (deal.phase === "playing") parts.push(t("game.tricksWonLabel", { count: deal.tricksWon[seat] }));
  } else if (deal.phase === "playing" || deal.phase === "discarding" || deal.phase === "choosingTrump") {
    parts.push(t("game.tricksWonLabel", { count: deal.tricksWon[seat] }));
  }
  return parts.join(" · ");
}

// Turn order goes counter-clockwise (packages/shared-types/src/players.ts —
// nextSeat is seat+1 mod 3, "modeled as counter-clockwise"). On screen,
// counter-clockwise from the viewer's own seat means the next player to act
// sits to their right, and the seat before them (who just acted, or will act
// after the right-hand seat) sits to their left.
function seatPosClass(offsetFromMe: 1 | 2): string {
  return offsetFromMe === 1 ? "seat-right" : "seat-left";
}

// Bot seats get a client-side, translated placeholder name instead of
// trusting the server's literal (Serbian-only) displayName — see
// MeksikoRoom.ts's "AI igrač" — so the name tracks whatever language is
// currently selected, same as everything else on screen.
function seatDisplayName(info: SeatInfo): string {
  return info.isBot ? t("game.botDisplayName") : info.displayName;
}

function renderGame(state: AppState, view: MatchView): void {
  const { deal, mySeat, seats } = view;
  const oppOffsets: (1 | 2)[] = [1, 2];

  const seatsHtml = oppOffsets
    .map((offset) => {
      const seat = ((mySeat + offset) % 3) as 0 | 1 | 2;
      const info = seats[seat];
      const isTurn = actingSeat(deal) === seat;
      const count = deal.opponentCardCounts[seat];
      return `
        <div class="seat ${seatPosClass(offset)}">
          ${renderReactionBubble(state, seat)}
          <div class="seat-label ${isTurn ? "turn" : ""}">
            ${renderAvatar(info.photoUrl, info.displayName, "avatar-seat")}
            <div class="seat-label-text">
              <div class="seat-label-name">
                ${escapeHtml(seatDisplayName(info))}
                ${declarerBadgeHtml(deal, seat)}
                ${info.isBot && info.connected ? `<span class="bot-badge idle">${t("game.botBadgeIdle")}</span>` : ""}
                ${info.isBot && !info.connected ? `<span class="bot-badge">${t("game.botBadgeFull")}</span>` : ""}
                ${!info.connected && !info.isBot ? '<span class="disconnected-badge">⏳</span>' : ""}
              </div>
              <div class="seat-label-meta">${seatMetaLabel(view, seat)}</div>
            </div>
          </div>
          <div class="opponent-hand">${Array.from({ length: count })
            .map(() => `<img src="${CARD_BACK_URL}" alt="${t("game.cardAlt")}" />`)
            .join("")}</div>
        </div>
      `;
    })
    .join("");

  const trickHtml = renderTrickArea(view);

  // Per-player score now lives next to each name (seat-label-meta / the
  // topbar below) instead of a separate bar — this stays up top only for
  // info that isn't tied to one specific seat.
  const scoreboardParts = [
    deal.trump
      ? `<span class="trump-indicator" style="color:${SUIT_UI_COLOR[deal.trump]}">${t("game.trumpLabel", { suit: t(`suit.${deal.trump}`) })}</span>`
      : "",
    deal.isMexico ? `<span class="mexico-badge">${t("game.mexicoLabel")}</span>` : "",
  ].filter(Boolean);
  const scoreboardHtml = scoreboardParts.length > 0 ? `<div class="scoreboard">${scoreboardParts.join("")}</div>` : "";

  const myHandHtml = renderMyHand(state, deal, mySeat);
  const phasePanelHtml = renderPhasePanel(state, view);
  const matchEndHtml = view.matchEnded ? renderMatchEnd(view) : "";
  const turnTimerHtml = renderTurnTimer(view);
  const idleBotBannerHtml = renderIdleBotBanner(view);
  const talonRevealHtml = renderTalonReveal(view);
  const myReactionBubbleHtml = renderReactionBubble(state, mySeat, "below");
  const reactionDialogHtml = state.reactionMenuOpen ? renderReactionDialog() : "";

  el(`
    <div class="topbar">
      <span class="topbar-identity">
        ${renderAvatar(state.photoUrl, state.displayName, "avatar-md")}${escapeHtml(state.displayName)}
        ${declarerBadgeHtml(deal, mySeat)}
        <span class="seat-label-meta">${seatMetaLabel(view, mySeat)}</span>
        ${myReactionBubbleHtml}
      </span>
      <span class="coin-balance">🪙 ${state.coinBalance}</span>
      <button class="secondary reaction-button" data-action="toggle-reactions">${t("reactions.sendButton")}</button>
      <button class="secondary" data-action="open-rules">📖</button>
      <button class="secondary" data-action="open-settings">⚙️</button>
      <button class="secondary" data-action="leave-match">${t("game.leaveMatch")}</button>
    </div>
    <div class="table">
      ${scoreboardHtml}
      ${turnTimerHtml}
      ${seatsHtml}
      ${trickHtml}
      ${talonRevealHtml}
      ${idleBotBannerHtml}
      ${phasePanelHtml}
      ${myHandHtml}
    </div>
    ${reactionDialogHtml}
    ${matchEndHtml}
    ${state.lastError ? `<div class="error-banner">${escapeHtml(state.lastError)}</div>` : ""}
    ${state.rulesOpen ? renderRulesModal() : ""}
    ${state.settingsOpen ? renderSettingsModal(state) : ""}
  `);
}

// Same "consume the animation once" idea as heldTrick/enteringCardKey above:
// every incidental re-render (e.g. any other player's turn/action, which
// rebuilds this bubble's DOM node from scratch via el()) would otherwise
// replay the reactionPop keyframe for the bubble's whole REACTION_DISPLAY_MS
// lifetime instead of just once when it first appears. Keyed by seat since
// each seat shows at most one bubble at a time.
const reactionAnimatedTokens = new Map<SeatIndex, number>();

// GAME_SPEC.md reactions feature — a one-shot speech-bubble above whichever
// seat sent a predefined text+emoji reaction (see shared-types/reactions.ts),
// auto-cleared client-side after REACTION_DISPLAY_MS (appStore.ts). Own seat
// lives in the topbar (top of the screen, nothing above it to float over),
// so its bubble points "below" toward the name instead of "above" it.
function renderReactionBubble(state: AppState, seat: SeatIndex, placement: "above" | "below" = "above"): string {
  const active = state.activeReactions[seat];
  if (!active) return "";
  const def = REACTIONS.find((r) => r.id === active.reactionId);
  if (!def) return "";
  const alreadyAnimated = reactionAnimatedTokens.get(seat) === active.token;
  reactionAnimatedTokens.set(seat, active.token);
  const label = t(`reaction.${def.id}`);
  return `
    <div class="reaction-bubble reaction-bubble-${placement}${alreadyAnimated ? " no-pop" : ""}">
      <span class="reaction-bubble-emoji">${def.emoji}</span>
      ${label ? `<span class="reaction-bubble-label">${escapeHtml(label)}</span>` : ""}
    </div>
  `;
}

function renderReactionDialog(): string {
  const buttons = REACTIONS.map((r) => {
    const label = t(`reaction.${r.id}`);
    return `
      <button class="reaction-option" data-action="send-reaction" data-reaction="${r.id}">
        <span class="reaction-option-emoji">${r.emoji}</span>
        ${label ? `<span class="reaction-option-label">${escapeHtml(label)}</span>` : ""}
      </button>
    `;
  }).join("");
  return `
    <div class="reaction-overlay" data-action="close-reactions">
      <div class="reaction-dialog" data-action="noop">
        <h2>${t("reactions.sendButton")}</h2>
        <div class="reaction-grid">${buttons}</div>
        <button class="secondary" data-action="close-reactions">${t("common.close")}</button>
      </div>
    </div>
  `;
}

// GAME_SPEC.md §6 — the bid winner opens the talon publicly; everyone sees
// these exact 2 cards until the bid winner discards (the 2 cards actually
// discarded afterwards are never sent to the client — see game-engine's
// buildDealView / DealState.discardPile).
function renderTalonReveal(view: MatchView): string {
  const { deal } = view;
  if (deal.phase !== "discarding" || !deal.talonCards) return "";
  const bidderName = deal.bidWinner !== null ? seatDisplayName(view.seats[deal.bidWinner]) : "";
  return `
    <div class="talon-reveal">
      <div class="talon-reveal-label">${t("game.talonRevealLabel", { bidder: escapeHtml(bidderName) })}</div>
      <div class="talon-reveal-cards">${deal.talonCards.map((c) => cardImg(c)).join("")}</div>
    </div>
  `;
}

// Mirrors seatPosClass's counter-clockwise mapping: the seat that acts right
// after me (offset 1) sits on my right, the one before me on my left.
function vertexName(offsetFromMe: number): "bottom" | "left" | "right" {
  return offsetFromMe === 0 ? "bottom" : offsetFromMe === 1 ? "right" : "left";
}

/**
 * The engine resolves a finished trick and starts the next one in the same
 * state update (game-engine/src/match.ts) — so the 3rd card of a trick never
 * actually appears in DealView.currentTrick, it goes straight to
 * completedTricks. This holds the just-finished trick on screen for a beat
 * (so the last card played is actually visible) before sweeping it away
 * toward the winner, purely as a display concern — the server-authoritative
 * game state itself is never delayed or altered by this.
 */
function syncTrickAnimationState(view: MatchView): void {
  const { deal } = view;

  if (deal.dealNumber !== lastSeenDealNumber) {
    // The just-finished deal's own last trick never appears in this deal's
    // currentTrick/completedTricks — they belong to a fresh deal that's
    // already in "bidding". CompletedDealSummary.lastTrick is the only place
    // that last card is still available, so hold+sweep it here exactly like
    // every other trick instead of letting it disappear unseen.
    const finished = view.lastCompletedDeal;
    const showFinalTrick = finished !== null && finished.dealNumber === lastSeenDealNumber;
    lastSeenDealNumber = deal.dealNumber;
    lastSeenCompletedCount = deal.completedTricks.length;
    lastCurrentTrickRef = deal.currentTrick;
    enteringCardKey = null;
    if (heldTrickTimer) clearTimeout(heldTrickTimer);
    heldTrickTimer = null;

    if (showFinalTrick) {
      const { cards, winner } = finished!.lastTrick;
      heldTrick = { cards, winner, phase: "hold" };
      heldTrickTimer = setTimeout(() => {
        if (heldTrick) heldTrick = { ...heldTrick, phase: "sweep-start" };
        renderApp(useAppStore.getState());
        heldTrickTimer = setTimeout(() => {
          heldTrick = null;
          heldTrickTimer = null;
          renderApp(useAppStore.getState());
        }, TRICK_SWEEP_MS);
      }, TRICK_HOLD_MS);
    } else {
      heldTrick = null;
    }
    return;
  }

  const completedCount = deal.completedTricks.length;
  if (completedCount > lastSeenCompletedCount) {
    lastSeenCompletedCount = completedCount;
    if (heldTrick === null) {
      const finished = deal.completedTricks[completedCount - 1]!;
      heldTrick = { cards: finished.cards, winner: finished.winner, phase: "hold" };
      if (heldTrickTimer) clearTimeout(heldTrickTimer);
      heldTrickTimer = setTimeout(() => {
        if (heldTrick) heldTrick = { ...heldTrick, phase: "sweep-start" };
        renderApp(useAppStore.getState());
        heldTrickTimer = setTimeout(() => {
          heldTrick = null;
          heldTrickTimer = null;
          renderApp(useAppStore.getState());
        }, TRICK_SWEEP_MS);
      }, TRICK_HOLD_MS);
    }
  }

  if (deal.currentTrick !== lastCurrentTrickRef) {
    const prevCards = lastCurrentTrickRef?.cards ?? [];
    const newCards = deal.currentTrick?.cards ?? [];
    enteringCardKey = newCards.length === prevCards.length + 1 ? `${newCards[newCards.length - 1]!.seat}:${cardId(newCards[newCards.length - 1]!.card)}` : null;
    lastCurrentTrickRef = deal.currentTrick;
  }
}

function renderTrickArea(view: MatchView): string {
  syncTrickAnimationState(view);
  const { deal, mySeat, seats } = view;

  const cards: PlayedCard[] = heldTrick?.cards ?? deal.currentTrick?.cards ?? [];
  const sweepVertex =
    heldTrick?.phase === "sweep-start" || heldTrick?.phase === "sweep-done"
      ? vertexName((((heldTrick.winner - mySeat) % 3) + 3) % 3)
      : null;
  // Consume the one-shot triggers immediately: this render is the only one
  // that gets to play the animation — any later re-render (e.g. the turn
  // countdown's periodic tick) must find them already "used up" and render
  // the resting/final state instead, or the CSS animation would restart from
  // scratch every time and look like it's endlessly jumping (see the note above).
  const isSweepStart = heldTrick?.phase === "sweep-start";
  if (isSweepStart && heldTrick) heldTrick = { ...heldTrick, phase: "sweep-done" };
  const consumedEnteringCardKey = enteringCardKey;
  enteringCardKey = null;

  return `
    <div class="trick-area">
      ${cards
        .map((pc) => {
          const vertex = vertexName((((pc.seat - mySeat) % 3) + 3) % 3);
          const animClass = sweepVertex
            ? isSweepStart
              ? `sweep-${sweepVertex}`
              : `swept-${sweepVertex}`
            : !heldTrick && consumedEnteringCardKey === `${pc.seat}:${cardId(pc.card)}`
              ? `enter-${vertex}`
              : "";
          return `
        <div class="trick-card trick-${vertex}">
          <div class="trick-card-anim ${animClass}">
            ${cardImg(pc.card)}
            <span class="who">${escapeHtml(seatDisplayName(seats[pc.seat]))}</span>
          </div>
        </div>
      `;
        })
        .join("")}
    </div>
  `;
}

// GAME_SPEC.md §10 — the countdown is visible to every seat, not just the
// one whose turn it is, so all players can see how long the acting player has left.
function renderTurnTimer(view: MatchView): string {
  if (view.turnDeadline === null) return "";
  const seat = actingSeat(view.deal);
  if (seat === null) return "";
  const remainingMs = Math.max(0, view.turnDeadline - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);
  const pct = Math.max(0, Math.min(100, (remainingMs / view.turnTimeoutMs) * 100));
  const name = seat === view.mySeat ? t("game.yourTurnLabel") : seatDisplayName(view.seats[seat]);
  return `
    <div class="turn-timer">
      <div class="turn-timer-label">${t("game.turnTimerLabel", { name: escapeHtml(name), remainingSec })}</div>
      <div class="turn-timer-bar"><div class="turn-timer-fill" style="width:${pct}%"></div></div>
    </div>
  `;
}

/**
 * Updates the already-rendered countdown in place instead of going through a
 * full renderApp(). main.ts ticks this every 250ms so the seconds visibly
 * count down between server state pushes — calling renderApp() there instead
 * would tear down and recreate every element on screen 4x/second, which
 * restarted every CSS :hover transition (card lift, button lift) each time,
 * making anything under the cursor visibly judder/bounce. This tick only
 * ever touches these two text/style properties, so the rest of the DOM
 * (hand cards, buttons, trick area, …) is left completely alone between real
 * state changes.
 */
export function tickTurnTimer(view: MatchView): void {
  if (view.turnDeadline === null) return;
  const seat = actingSeat(view.deal);
  if (seat === null) return;
  const label = document.querySelector<HTMLElement>(".turn-timer-label");
  const fill = document.querySelector<HTMLElement>(".turn-timer-fill");
  if (!label || !fill) return; // not currently on screen (e.g. a deal-summary overlay is covering it)

  const remainingMs = Math.max(0, view.turnDeadline - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);
  const pct = Math.max(0, Math.min(100, (remainingMs / view.turnTimeoutMs) * 100));
  const name = seat === view.mySeat ? t("game.yourTurnLabel") : seatDisplayName(view.seats[seat]);
  label.textContent = t("game.turnTimerLabel", { name, remainingSec });
  fill.style.width = `${pct}%`;
}

// GAME_SPEC.md §10 — the timed-out player themselves also needs to know the
// AI has taken over on their behalf, and how to get control back.
function renderIdleBotBanner(view: MatchView): string {
  const mine = view.seats[view.mySeat];
  if (!mine.isBot || !mine.connected) return "";
  return `<div class="idle-bot-banner">${t("game.idleBotTakeover")}</div>`;
}

function renderMyHand(state: AppState, deal: DealView, mySeat: number): string {
  const isMyTurn = actingSeat(deal) === mySeat;
  // Client-side-only display order: karo, tref, srce, list left to right,
  // strongest card first within each suit. Purely cosmetic — see
  // sortHandForDisplay — the server's hand order and legality are unaffected.
  const sortedHand = sortHandForDisplay(deal.myHand);

  if (deal.phase === "discarding" && deal.bidWinner === mySeat) {
    if (selectedDiscardDealNumber !== deal.dealNumber) {
      selectedDiscard = [];
      selectedDiscardDealNumber = deal.dealNumber;
    }
    // GAME_SPEC.md §6 — mark which 2 of these 12 cards came from the talon,
    // so the bid winner can tell them apart while deciding what to discard
    // (they're free to discard any 2, not necessarily these).
    const talonIds = new Set((deal.talonCards ?? []).map((c) => cardId(c)));
    const cards = sortedHand
      .map((c) => {
        const id = cardId(c);
        const selected = selectedDiscard.includes(id);
        const fromTalon = talonIds.has(id);
        const classes = ["legal", selected ? "selected" : "", fromTalon ? "from-talon" : ""].filter(Boolean).join(" ");
        return `
          <div class="hand-card-wrap">
            <img src="${cardImageUrl(c)}" alt="${t("game.cardAlt")}" data-action="toggle-discard" data-card="${id}" class="${classes}" />
            ${fromTalon ? `<span class="talon-tag">${t("game.fromTalon")}</span>` : ""}
          </div>
        `;
      })
      .join("");
    return `<div class="my-hand">${cards}</div>`;
  }

  const legalIds = new Set<string>();
  if (deal.phase === "playing" && isMyTurn && deal.currentTrick) {
    for (const c of legalPlays(deal.myHand, deal.currentTrick, deal.trump)) legalIds.add(cardId(c));
  }

  const cards = sortedHand
    .map((c) => {
      const id = cardId(c);
      const clickable = deal.phase === "playing" && isMyTurn && legalIds.has(id);
      return cardImg(c, {
        classes: [clickable ? "legal" : deal.phase === "playing" && isMyTurn ? "illegal" : ""],
        dataCard: clickable ? id : undefined,
      });
    })
    .join("");
  return `<div class="my-hand">${cards}</div>`;
}

// Starts the post-deal pause exactly once per newly-seen deal (guarded by
// dealPausedForDealNumber) and schedules the one re-render needed to lift it
// — see DEAL_END_PAUSE_MS above.
function syncDealPause(view: MatchView): void {
  if (view.lastCompletedDeal && dealPausedForDealNumber !== view.deal.dealNumber) {
    dealPausedForDealNumber = view.deal.dealNumber;
    dealPauseUntil = Date.now() + DEAL_END_PAUSE_MS;
    setTimeout(() => renderApp(useAppStore.getState()), DEAL_END_PAUSE_MS);
  }
}

function renderPhasePanel(state: AppState, view: MatchView): string {
  const { deal, mySeat } = view;
  syncDealPause(view);
  if (dealPausedForDealNumber === deal.dealNumber && Date.now() < dealPauseUntil) {
    // No popup — the score next to each name already updated; this is just
    // a beat to notice it before the next deal's bidding becomes interactive.
    return `<div class="side-panel"><p>${t("game.nextDealPause")}</p></div>`;
  }
  const isMyTurn = actingSeat(deal) === mySeat;

  if (deal.phase === "bidding") {
    const legal = new Set(legalBids(deal.bidding));
    const canPassNow = canPass(deal.bidding);
    const historyHtml = deal.bidding.history
      .map((h) => `${escapeHtml(seatDisplayName(view.seats[h.seat]))}: ${bidValueLabel(h.value)}`)
      .join(" · ");
    if (!isMyTurn) {
      return `<div class="side-panel"><p>${t("game.biddingInProgress")}</p><p class="bid-history">${historyHtml}</p></div>`;
    }
    const buttons = [5, 6, 7, 8, 9, 10, 20]
      .map((v) => {
        const disabled = !legal.has(v as never);
        const label = bidValueLabel(v);
        return `<button data-action="bid" data-value="${v}" ${disabled ? "disabled" : ""}>${label}</button>`;
      })
      .join("");
    return `
      <div class="side-panel">
        <p>${t("game.yourBid")}</p>
        <div class="bid-ladder">${buttons}
          <button class="secondary" data-action="pass" ${canPassNow ? "" : "disabled"}>${t("game.passButton")}</button>
        </div>
        <p class="bid-history">${historyHtml}</p>
      </div>
    `;
  }

  if (deal.phase === "discarding") {
    if (deal.bidWinner !== mySeat) {
      return `<div class="side-panel"><p>${t("game.someoneDiscarding", { name: escapeHtml(seatDisplayName(view.seats[deal.bidWinner!])) })}</p></div>`;
    }
    // No popup here — picking 2 cards directly in the hand below (they lift
    // up when selected, see .my-hand img.selected) submits the discard the
    // moment the 2nd one is picked (see main.ts's "toggle-discard" handler).
    return "";
  }

  if (deal.phase === "choosingTrump") {
    if (deal.bidWinner !== mySeat) {
      return `<div class="side-panel side-panel-high"><p>${t("game.someoneChoosingTrump", { name: escapeHtml(seatDisplayName(view.seats[deal.bidWinner!])) })}</p></div>`;
    }
    const buttons = SUITS.map(
      (s) => `<button data-action="choose-trump" data-suit="${s}" style="color:${SUIT_COLOR[s]}">${t(`suit.${s}`)}</button>`,
    ).join("");
    return `<div class="side-panel side-panel-high"><p>${t("game.chooseTrumpPrompt")}</p><div class="trump-choices">${buttons}</div></div>`;
  }

  return "";
}

function renderMatchEnd(view: MatchView): string {
  const winnerName = view.matchWinnerSeat !== null ? seatDisplayName(view.seats[view.matchWinnerSeat]) : "?";
  return `
    <div class="deal-summary">
      <div class="deal-summary-card">
        <h2>${t("matchEnd.title")}</h2>
        <p>${t("matchEnd.winner", { name: escapeHtml(winnerName) })}</p>
        <button data-action="back-to-lobby">${t("matchEnd.backToLobby")}</button>
      </div>
    </div>
  `;
}

export function getSelectedDiscard(): string[] {
  return selectedDiscard;
}

export function toggleDiscardSelection(cardIdStr: string): void {
  if (selectedDiscard.includes(cardIdStr)) {
    selectedDiscard = selectedDiscard.filter((id) => id !== cardIdStr);
  } else if (selectedDiscard.length < 2) {
    selectedDiscard = [...selectedDiscard, cardIdStr];
  }
}

export function resetDiscardSelection(): void {
  selectedDiscard = [];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

// Auth0 social login only (photoUrl is always null on Facebook/bot seats —
// see User.photoUrl) — falls back to a colored circle with the first letter
// of the display name, same idea everywhere it's used (topbar, seat labels,
// leaderboard rows, profile header).
//
// The Facebook Instant Games webview enforces its own Content-Security-Policy
// that only whitelists a handful of Facebook-owned/CDN domains for img-src —
// an Auth0 user's real photoUrl (e.g. Google's lh3.googleusercontent.com) is
// not on that list, so the <img> silently fails to load there even though the
// exact same URL works fine on the standalone web build. Rather than proxy
// every photo through our own domain, treat any viewer on the Facebook
// platform as if no photoUrl was ever provided — same initials fallback used
// for Facebook/bot users themselves.
function renderAvatar(photoUrl: string | null | undefined, displayName: string, sizeClass: string): string {
  const onFacebook = useAppStore.getState().platform?.kind === "facebook";
  if (photoUrl && !onFacebook) {
    return `<img class="avatar ${sizeClass}" src="${escapeHtml(photoUrl)}" alt="" referrerpolicy="no-referrer" />`;
  }
  const initial = (displayName.trim()[0] ?? "?").toUpperCase();
  return `<span class="avatar ${sizeClass} avatar-fallback">${escapeHtml(initial)}</span>`;
}

// Facebook Zero Permissions has no photoUrl for us to read (see renderAvatar's
// comment) — but FBInstant.overlayViews can render the real name/photo into
// an iframe *we never see the contents of*. That iframe has to be (re)created
// after each innerHTML replace (see the module-top comment on el()), so this
// only emits an empty, stably-sized mount point here; mountFacebookOverlays()
// below does the actual FBInstant call once the element exists in the DOM.
const FB_SELF_PORTRAIT_ID = "fb-self-portrait";

function renderSelfAvatar(state: AppState, sizeClass: string): string {
  if (state.platform?.kind === "facebook") {
    // Renders the initials fallback *inside* the mount point (not an empty
    // container) — mountFacebookOverlays()'s attempt to enhance this with the
    // real overlay is unverified/best-effort (see facebook.ts) and fails
    // silently, so this fallback is what most players will actually see.
    const initial = (state.displayName.trim()[0] ?? "?").toUpperCase();
    return `<span id="${FB_SELF_PORTRAIT_ID}" class="avatar ${sizeClass} avatar-fallback">${escapeHtml(initial)}</span>`;
  }
  return renderAvatar(state.photoUrl, state.displayName, sizeClass);
}

// Call once per render() pass, after el() has replaced the DOM — see main.ts.
// No-op unless the topbar's portrait mount point is actually on screen right
// now (only the lobby renders it today) and we're running inside Facebook.
export function mountFacebookOverlays(state: AppState): void {
  if (state.platform?.kind !== "facebook") return;
  if (document.getElementById(FB_SELF_PORTRAIT_ID)) {
    state.platform.renderPlayerPortrait(FB_SELF_PORTRAIT_ID, state.userId ?? "");
  }
}
